import { useState, useRef, useCallback, useEffect } from "react";
import { useDebounce } from "../../Dropshipping/Products/hooks/useDebounce";
import AtecoReferenceService from "../../../../../service/AtecoReferenceService";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;
const MAX_RESULTS = 20;

function normalizeItem(item) {
  if (!item || typeof item !== "object") return item;
  return {
    ...item,
    atecoCode: item.atecoCode ?? item.code ?? "",
    descriptionItalian: item.descriptionItalian ?? item.description ?? "",
  };
}

function normalizeList(list) {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeItem);
}

function filterByQuery(items, query) {
  if (!items || !query) return items || [];
  const q = query.toLowerCase();
  return items.filter((item) => {
    const code = (item.atecoCode || "").toLowerCase();
    const desc = (item.descriptionItalian || "").toLowerCase();
    return code.includes(q) || desc.includes(q);
  });
}

function findLongestPrefixKey(cacheRef, query) {
  let best = null;
  for (const key of cacheRef.current.keys()) {
    if (query.startsWith(key) && (!best || key.length > best.length)) best = key;
  }
  return best;
}

export function useAtecoSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef(new Map());
  const serviceRef = useRef(new AtecoReferenceService());
  const fetchSearch = useCallback(async (term) => {
    if (term.length < MIN_QUERY_LENGTH) return;
    setLoading(true);
    try {
      const data = await serviceRef.current.search(term);
      const raw = Array.isArray(data) ? data : data?.result ?? data?.data ?? data?.content ?? [];
      const list = normalizeList(raw);
      cacheRef.current.set(term, list);
      setResults(filterByQuery(list, term).slice(0, MAX_RESULTS));
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);
  const debouncedFetch = useDebounce(fetchSearch, DEBOUNCE_MS);
  const debouncedFetchRef = useRef(debouncedFetch);
  debouncedFetchRef.current = debouncedFetch;

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setResults([]);
      return;
    }
    const prefixKey = findLongestPrefixKey(cacheRef, q);
    if (prefixKey != null) {
      const cached = cacheRef.current.get(prefixKey) || [];
      setResults(filterByQuery(cached, q).slice(0, MAX_RESULTS));
      return;
    }
    debouncedFetchRef.current(q);
  }, [query]);

  return { query, setQuery, results, loading };
}
